import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { I18n } from "@aws-amplify/core";
import { withStyles } from "@material-ui/styles";
import update from "immutability-helper";
import { get, debounce, cloneDeep, map, filter, size } from "lodash";

import { fetchPost } from "../../../api/fetch";

import { setFieldValue } from "../../../actions/siteEntranceExit/correctConfirmValue";

import Grid from "@material-ui/core/Grid";
import Box from "@material-ui/core/Box";
import Radio from "@material-ui/core/Radio";
import CircularProgress from "@material-ui/core/CircularProgress";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";

import ProfileMatch from "./ProfileMatch";
import CustomButton from "../../Common/CustomButton";
import CustomInput from "../../Common/CustomInput";

import { getLov } from "../../../utils/lov";

import images from "../../../assets/img/images";

const styles = {
  endAdornment: {
    padding: "5px",
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
};

const PotentialMatches = (props) => {
  const { dispatch, classes, searchValues, profile, setOverrideData } = props;

  const [loading, setLoading] = React.useState(false);
  const [lov, setLov] = React.useState({});

  const searchValue =
    props.siteEntranceExit[props.section].correctConfirmValue.searchValue;
  const selectedProfile =
    props.siteEntranceExit[props.section].correctConfirmValue.selectedProfile;
  const potentialMatches =
    props.siteEntranceExit[props.section].correctConfirmValue.potentialMatches;

  React.useEffect(() => {
    const ac = new AbortController();

    const fetchLov = async () => {
      let lov135 = await getLov(135);
      if (!ac.signal.aborted) {
        setLov({
          135: lov135,
        });
      }
    };
    fetchLov();

    return () => {
      ac.abort();
    };
     
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (
      !searchValue &&
      (searchValues.ocr.length > 0 || searchValues.lpr.length > 0)
    ) {
      if (
        !potentialMatches[getSearchValueKey(searchValues.ocr, searchValues.lpr)]
      ) {
        searchMatches();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(searchValues), searchValue]);

  const getSearchValueKey = (ocrs, lprs) => {
    let key = "";
    if (size(ocrs) > 0) {
      key += ocrs.join(" ");
    } else if (size(lprs) > 0) {
      key += lprs.join(" ");
    }
    return key;
  };

  const searchMatches = async (newSearchValue = null, potMatches = null) => {
    let searchOcr,
      searchLpr = [];
    if (newSearchValue) {
      searchOcr = newSearchValue.split(" ");
      searchLpr = newSearchValue.split(" ");
    } else {
      searchOcr = searchValues.ocr;
      searchLpr = searchValues.lpr;
    }
    try {
      let res = await fetchPost("profiles/search", {
        types: [get(profile, ["type", "id"])],
        lpr: searchLpr,
        ocr: searchOcr,
      });

      res = filter(res, (item) => get(item, ["id"]) !== get(profile, ["id"]));

      dispatch(
        setFieldValue(
          props.section,
          "potentialMatches",
          update(potMatches || potentialMatches, {
            [getSearchValueKey(searchOcr, searchLpr)]: {
              $set: res,
            },
          })
        )
      );
      setLoading(false);
    } catch (e) {
      console.log(e);
    }
  };

  const handleChange = (e) => {
    if (e.target.id === "searchValue") {
      dispatch(setFieldValue(props.section, "searchValue", e.target.value));
      if (e.target.value && !potentialMatches[e.target.value]) {
        let potMatches = cloneDeep(potentialMatches);
        debouncedSearchMatches(e.target.value, potMatches);
      }
    } else {
      dispatch(setFieldValue(props.section, "selectedProfile", e.target.value));
    }
  };

  const getSelectedProfile = () => {
    if (selectedProfile === "create") {
      return selectedProfile;
    }
    let sp = potentialMatches[
      searchValue || getSearchValueKey(searchValues.ocr, searchValues.lpr)
    ].filter((m) => m.id === selectedProfile);
    if (sp.length > 0) {
      sp = sp[0];
      return sp;
    }
    return null;
  };

  const debouncedSearchMatches = React.useCallback(
    debounce((newSearchValue, potMatches) => {
      setLoading(true);
      searchMatches(newSearchValue, potMatches);
    }, 500),
    []
  );

  return (
    <Grid container direction="column">
      <Grid
        item
        className="text-87 bold-500 text-center"
        style={{ margin: "0px 42px 5px" }}
      >
        {I18n.get("site_potentialMatches")}
      </Grid>
      <Grid item style={{ padding: "0px 0px 10px 42px" }}>
        <CustomInput
          name="searchValue"
          autocomplete="off"
          value={searchValue}
          onChange={handleChange}
          startAdornment={<img src={images.searchIcon} alt="search" />}
          endAdornment={
            searchValue ? (
              <InputAdornment position="end">
                <IconButton
                  edge="end"
                  onClick={() =>
                    dispatch(setFieldValue(props.section, "searchValue", ""))
                  }
                  style={{ opacity: "0.54" }}
                  classes={{ root: classes.endAdornment }}
                >
                  <img
                    src={images.closeIcon}
                    alt="search"
                    width="12px"
                    height="12px"
                  />
                </IconButton>
              </InputAdornment>
            ) : (
              <></>
            )
          }
          placeholder={I18n.get("site_searchOcrLpr")}
        />
      </Grid>
      {loading ? (
        <Grid
          item
          className="text-center"
          style={{
            height: "200px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress color="primary" size={20} />
        </Grid>
      ) : (
        <>
          <Grid item>
            <Grid
              container
              direction="column"
              wrap="nowrap"
              style={{
                maxHeight: "400px",
                overflow: "auto",
              }}
            >
              {map(
                potentialMatches[
                  searchValue ||
                    getSearchValueKey(searchValues.ocr, searchValues.lpr)
                ],
                (p) => (
                  <Grid item key={p.id} style={{ marginBottom: "15px" }}>
                    <Grid container alignItems="center">
                      <Grid item>
                        <Radio
                          checked={selectedProfile === p.id}
                          onChange={handleChange}
                          value={p.id}
                          color="primary"
                        />
                      </Grid>
                      <ProfileMatch
                        profile={p}
                        lov={lov}
                        searchValue={searchValue}
                      />
                    </Grid>
                  </Grid>
                )
              )}
              <Grid item>
                <Grid container alignItems="center" wrap="nowrap">
                  <Grid item>
                    <Radio
                      checked={selectedProfile === "create"}
                      onChange={handleChange}
                      value="create"
                      color="primary"
                    />
                  </Grid>
                  <Grid item>
                    <Box
                      component="span"
                      className="text-54"
                      onClick={() =>
                        dispatch(
                          setFieldValue(
                            props.section,
                            "selectedProfile",
                            "create"
                          )
                        )
                      }
                      style={{ cursor: "pointer" }}
                    >
                      {I18n.get("site_noProfileMsg2")}
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid item className="text-center" style={{ marginTop: "10px" }}>
            <CustomButton
              variant="contained"
              color="primary"
              startIcon={
                <img
                  src={
                    selectedProfile
                      ? images.overrideIconWhite
                      : images.overrideIconGreyLight
                  }
                  alt="override"
                />
              }
              onClick={() => {
                setOverrideData({
                  currentProfile: profile,
                  newProfile: getSelectedProfile(),
                });
              }}
              disabled={!selectedProfile}
              style={{ marginBottom: 0 }}
            >
              {I18n.get("site_override")}
            </CustomButton>
          </Grid>
        </>
      )}
    </Grid>
  );
};

PotentialMatches.propTypes = {
  searchValues: PropTypes.shape({
    ocr: PropTypes.array.isRequired,
    lpr: PropTypes.array.isRequired,
  }).isRequired,
  focusElement: PropTypes.object.isRequired,
  profile: PropTypes.object.isRequired,
  setOverrideData: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => {
  return {
    siteEntranceExit: state.siteEntranceExit,
  };
};

export default connect(mapStateToProps)(withStyles(styles)(PotentialMatches));
